"use client";

/**
 * "Open in app" — the web→native handoff on an iPhone or iPad, including the
 * case that actually happens most: a Hub link tapped **inside Slack**.
 *
 * Someone taps a Hub link in a Slack card on their phone, lands in Slack's
 * in-app browser, and pinch-zooms their way through a desktop CRM. There are two
 * good destinations from there and the person knows which one they want:
 *
 *   - the **native Hub screen** for that record, or
 *   - **back to the Slack conversation** about that customer.
 *
 * This bar offers both and lets them choose. It is not a guess.
 *
 * ## Why this cannot rely on universal links
 *
 * Universal links do NOT fire from inside a WKWebView, which is what Slack's
 * in-app browser is. So even with `apple-app-site-association` published and
 * verified, a Hub link tapped in Slack will never hand off to the app on its
 * own. A `wtchub://` anchor DOES work there — tapping one asks the system to
 * open the app — which is why the custom scheme is the mechanism here and the
 * universal link is the mechanism everywhere else.
 *
 * ## Deliberate choices
 *
 * - **No automatic redirect the first time.** An automatic jump breaks the
 *   person who is deliberately in the browser and, on a link shared into a
 *   channel, hijacks every recipient who has the app. The user taps.
 * - **It remembers.** Choose "always" once and subsequent Hub links open the app
 *   directly. That is a user-granted auto-redirect, which is the only kind worth
 *   having.
 * - **It only appears where the app has a real screen.** The target comes from
 *   the shared table in `@wtc/hub-client/deep-links` — the same table the app
 *   parses and the AASA file publishes — so it cannot offer a jump into a screen
 *   that does not exist.
 * - **UA sniffing is fine HERE.** It gates an offer, not access: a false
 *   negative shows nothing (status quo), a false positive shows a bar that does
 *   nothing when tapped.
 */

import { useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { matchHubWebPath, hubAppURL } from "@wtc/hub-client/deep-links";

const DISMISS_KEY = "wtc-hub:open-in-app-dismissed-until";
const ALWAYS_KEY = "wtc-hub:open-in-app-always";
const DISMISS_DAYS = 30;

/** Entity types `/api/slack/channel-link` can resolve a customer channel for. */
const SLACK_LINKABLE_ENTITIES = new Set([
  "companies",
  "contacts",
  "deals",
  "tickets",
  "tasks",
  "communications",
]);

/**
 * iPhone / iPad, and not already running as an installed home-screen app.
 *
 * iPadOS 13+ reports a desktop Safari user agent, so the platform check alone
 * misses every iPad — the `MacIntel` + touch-points pair is the standard way
 * back. Guarded for SSR, where neither object exists.
 */
function isIOSBrowser(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;

  const ua = navigator.userAgent;
  const isIPhone = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (!isIPhone && !isIPadOS) return false;

  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  return !standalone;
}

/**
 * Slack's in-app browser. Slack appends `Slack/<build>` (and on iOS also
 * `com.tinyspeck.chatlyio`) to the WebView user agent.
 *
 * Worth detecting separately because it is the one context where the OS will
 * NOT hand a universal link to the app — so the bar has to do the whole job,
 * and it can also offer the trip back to Slack.
 */
function isSlackInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Slack|com\.tinyspeck\.chatlyio/i.test(navigator.userAgent);
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function dismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (raw === null) return false;
    const until = Number.parseInt(raw, 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    // Private mode / storage disabled. Showing the bar is the safe failure.
    return false;
  }
}

export function OpenInAppBanner() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const [inSlack, setInSlack] = useState(false);
  const [slackChannelURL, setSlackChannelURL] = useState<string | null>(null);

  const match = pathname === null ? null : matchHubWebPath(pathname);
  const appLink = match === null ? null : hubAppURL(match.target, match.id);

  // Effect, not render-time: `navigator` does not exist during SSR, and reading
  // it in render would hydrate-mismatch on every page.
  useEffect(() => {
    if (appLink === null) {
      setVisible(false);
      return;
    }
    if (!isIOSBrowser()) {
      setVisible(false);
      return;
    }

    setInSlack(isSlackInAppBrowser());

    // The user already said "always" — honour it instead of asking again. This
    // is the only path that navigates on its own, and it exists because they
    // asked for it.
    if (readFlag(ALWAYS_KEY)) {
      window.location.href = appLink;
      return;
    }

    setVisible(!dismissedRecently());
  }, [appLink, pathname]);

  // Is there a Slack conversation about this customer? Only asked for records,
  // and a 404 is the normal "no channel set" answer — not an error worth
  // surfacing. Aborted on unmount so a fast tab-away cannot set state late.
  useEffect(() => {
    setSlackChannelURL(null);
    if (!visible || match?.id === undefined) return;
    if (match.target.kind !== "entity") return;
    if (!SLACK_LINKABLE_ENTITIES.has(match.target.entity)) return;

    const controller = new AbortController();
    const params = new URLSearchParams({
      entityType: match.target.entity,
      entityId: match.id,
    });

    fetch(`/api/slack/channel-link?${params.toString()}`, {
      signal: controller.signal,
      credentials: "same-origin",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { url?: string } | null) => {
        const url = typeof data?.url === "string" ? data.url : null;
        if (url !== null) setSlackChannelURL(url);
      })
      .catch(() => {
        // Offline, aborted, or no channel. The bar simply does not offer Slack.
      });

    return () => controller.abort();
  }, [visible, match?.id, match?.target]);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      window.localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
      );
    } catch {
      // Nothing to do — the bar is gone for this page view either way.
    }
  }, []);

  const openAlways = useCallback(() => {
    try {
      window.localStorage.setItem(ALWAYS_KEY, "1");
    } catch {
      // Preference is a convenience; opening still works without it.
    }
    if (appLink !== null) window.location.href = appLink;
  }, [appLink]);

  if (!visible || appLink === null) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
      style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
      role="region"
      aria-label="Open this page in the WTC Hub app"
    >
      <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">
        {inSlack ? "Opened from Slack — where to?" : "Open this in the Hub app?"}
      </span>

      {slackChannelURL !== null && (
        <a
          href={slackChannelURL}
          className="rounded-md border border-slate-300 px-3 py-1.5 font-medium text-slate-700 dark:border-slate-600 dark:text-slate-200"
          rel="noreferrer"
        >
          Slack channel
        </a>
      )}

      <a
        href={appLink}
        className="rounded-md bg-[#245397] px-3 py-1.5 font-medium text-white"
        // Nothing about the target is derived from page content — `appLink` is
        // built from a closed table — but the app is a different context, so no
        // referrer.
        rel="noreferrer"
      >
        Hub app
      </a>

      <button
        type="button"
        onClick={openAlways}
        className="rounded-md px-2 py-1.5 text-slate-500 underline dark:text-slate-400"
      >
        Always
      </button>

      <button
        type="button"
        onClick={dismiss}
        className="rounded-md px-2 py-1.5 text-slate-500 dark:text-slate-400"
        aria-label="Not now — hide this for 30 days"
      >
        Not now
      </button>
    </div>
  );
}
